import { history } from "@umijs/max";
import { useSession } from "@zhiyue/core";
import React from "react";
import { OpenPlatformAccessTokenContext } from "@/contexts/openPlatform";
import {
  clearOpenPlatformToken,
  fetchOpenPlatformToken,
  type OpenPlatformTokenResult,
} from "@/services/openPlatformToken";

const publicPaths = ["/user/login", "/callback"];
const normalizeText = (value: unknown) => String(value ?? "").trim();

const OpenPlatformBootstrap: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading } = useSession();
  const [readyKey, setReadyKey] = React.useState("");
  const [tokenInfo, setTokenInfo] =
    React.useState<OpenPlatformTokenResult | null>(null);
  const tenantUuid = normalizeText(currentUser?.tenantUuid);
  const organizationUuid = normalizeText(
    (currentUser as (typeof currentUser & { organizationUuid?: string }) | null)
      ?.organizationUuid
  );
  const memberUuid = normalizeText(currentUser?.memberUuid);
  const userKey = [tenantUuid, organizationUuid, memberUuid].join("|");
  const bootstrapUser = React.useMemo(
    () =>
      currentUser && memberUuid
        ? { tenantUuid, organizationUuid, memberUuid }
        : null,
    [currentUser ? userKey : "", memberUuid]
  );
  const isPublicPath = publicPaths.includes(history.location.pathname);

  React.useEffect(() => {
    if (loading || isPublicPath) return;
    if (!bootstrapUser) {
      clearOpenPlatformToken();
      setReadyKey("");
      setTokenInfo(null);
      return;
    }

    let cancelled = false;
    void fetchOpenPlatformToken(bootstrapUser)
      .then((result) => {
        if (!cancelled) {
          setTokenInfo(result);
          setReadyKey(userKey);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTokenInfo(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bootstrapUser, isPublicPath, loading, userKey]);

  React.useEffect(() => {
    if (!bootstrapUser || readyKey !== userKey || !tokenInfo) return;

    const refreshDelay = Math.max(tokenInfo.expiresAt - Date.now() - 60_000, 0);
    const timer = window.setTimeout(() => {
      clearOpenPlatformToken();
      void fetchOpenPlatformToken(bootstrapUser)
        .then(setTokenInfo)
        .catch(() => {
          setTokenInfo(null);
          setReadyKey("");
        });
    }, refreshDelay);

    return () => window.clearTimeout(timer);
  }, [bootstrapUser, readyKey, tokenInfo, userKey]);

  if (isPublicPath || (!loading && !currentUser)) {
    return (
      <OpenPlatformAccessTokenContext.Provider value="">
        {children}
      </OpenPlatformAccessTokenContext.Provider>
    );
  }

  return (
    <OpenPlatformAccessTokenContext.Provider
      value={tokenInfo?.accessToken || ""}
    >
      {children}
    </OpenPlatformAccessTokenContext.Provider>
  );
};

export default OpenPlatformBootstrap;
